import React, { useMemo, useState } from 'react';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { Card } from '../components/SubscriptionAppCard';
import { SearchBar } from '../components/SearchBar';
import { TableFilter, Filter } from '../components/TableFilter';
import { ExportButton } from '../components/ExportButton';
import { Pagination } from '../components/SubscriptionAppPagination';
import { Plus, Download, MoreVertical, Calendar, Users, Wine, Trash2, XCircle, CheckCircle, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// Mock Data
interface Allocation {
  id: string;
  name: string;
  description: string;
  status: 'Draft' | 'Active' | 'Closed';
  productCount: number;
  startDate: string;
  endDate: string;
  claimed: number;
  totalAvailable: number;
  revenue: number;
  eligibleCount: number;
}
const mockAllocations: Allocation[] = [{
  id: '1',
  name: '2023 Reserve Shiraz Release',
  description: 'Exclusive access to our flagship red for Gold members',
  status: 'Active',
  productCount: 1,
  startDate: '1 Mar 2026',
  endDate: '15 Mar 2026',
  claimed: 142,
  totalAvailable: 200,
  revenue: 28400,
  eligibleCount: 485
}, {
  id: '2',
  name: 'Autumn Library Collection',
  description: 'Rare back-vintage wines from the cellar',
  status: 'Draft',
  productCount: 4,
  startDate: '20 Mar 2026',
  endDate: '10 Apr 2026',
  claimed: 0,
  totalAvailable: 350,
  revenue: 0,
  eligibleCount: 0
}, {
  id: '3',
  name: '2022 Single Vineyard Pinot',
  description: 'Limited release from the Block 4 vineyard',
  status: 'Closed',
  productCount: 1,
  startDate: '10 Jan 2026',
  endDate: '25 Jan 2026',
  claimed: 120,
  totalAvailable: 120,
  revenue: 14400,
  eligibleCount: 320
}, {
  id: '4',
  name: 'Summer Rosé Pack',
  description: '3-pack special for the holiday season',
  status: 'Closed',
  productCount: 3,
  startDate: '1 Dec 2025',
  endDate: '20 Dec 2025',
  claimed: 89,
  totalAvailable: 150,
  revenue: 8010,
  eligibleCount: 210
}, {
  id: '5',
  name: 'Vintner Exclusive — Museum Release',
  description: 'Aged Cabernet for our top tier members only',
  status: 'Active',
  productCount: 2,
  startDate: '5 Mar 2026',
  endDate: '30 Mar 2026',
  claimed: 18,
  totalAvailable: 50,
  revenue: 5400,
  eligibleCount: 50
}, {
  id: '6',
  name: 'New Vintage Tasting Pack',
  description: 'Sample the upcoming releases before anyone else',
  status: 'Draft',
  productCount: 6,
  startDate: 'TBD',
  endDate: '',
  claimed: 0,
  totalAvailable: 500,
  revenue: 0,
  eligibleCount: 0
}];
export function AllocationsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    status: [],
    dateRange: [],
    products: [],
    segment: []
  });
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  // Filter Configuration
  const availableFilters: Filter[] = [{
    id: 'status',
    label: 'Status',
    options: [{
      id: 'draft',
      label: 'Draft',
      value: 'Draft'
    }, {
      id: 'active',
      label: 'Active',
      value: 'Active'
    }, {
      id: 'closed',
      label: 'Closed',
      value: 'Closed'
    }],
    selectedValues: filterValues.status || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('status')
  }, {
    id: 'dateRange',
    label: 'Date Range',
    options: [{
      id: 'upcoming',
      label: 'Upcoming',
      value: 'upcoming'
    }, {
      id: 'active',
      label: 'Currently Active',
      value: 'active'
    }, {
      id: 'past',
      label: 'Past',
      value: 'past'
    }],
    selectedValues: filterValues.dateRange || [],
    multiSelect: false,
    isOpen: openFilterDropdowns.has('dateRange')
  }, {
    id: 'products',
    label: 'Products',
    options: [{
      id: '1',
      label: 'Single Product',
      value: '1'
    }, {
      id: 'multi',
      label: 'Multi-Product',
      value: 'multi'
    }],
    selectedValues: filterValues.products || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('products')
  }, {
    id: 'segment',
    label: 'Segment',
    options: [{
      id: 'gold',
      label: 'Gold Members',
      value: 'gold'
    }, {
      id: 'silver',
      label: 'Silver Members',
      value: 'silver'
    }, {
      id: 'club',
      label: 'Club Members',
      value: 'club'
    }],
    selectedValues: filterValues.segment || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('segment')
  }];
  // Filter Logic
  const filteredAllocations = useMemo(() => {
    return mockAllocations.filter(allocation => {
      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!allocation.name.toLowerCase().includes(query) && !allocation.description.toLowerCase().includes(query)) {
          return false;
        }
      }
      // Status Filter
      if (filterValues.status && filterValues.status.length > 0) {
        if (!filterValues.status.includes(allocation.status)) return false;
      }
      return true;
    });
  }, [searchQuery, filterValues]);
  // Handlers
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(new Set(filteredAllocations.map(a => a.id)));
    } else {
      setSelectedIds(new Set());
    }
  };
  const handleSelectRow = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    setFilterValues(prev => ({
      ...prev,
      [filterId]: Array.isArray(value) ? value : [value]
    }));
  };
  const handleFilterRemove = (filterId: string) => {
    const newAdded = new Set(addedFilters);
    newAdded.delete(filterId);
    setAddedFilters(newAdded);
    setFilterValues(prev => ({
      ...prev,
      [filterId]: []
    }));
  };
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpen = new Set(openFilterDropdowns);
    if (newOpen.has(filterId)) {
      newOpen.delete(filterId);
    } else {
      newOpen.add(filterId);
    }
    setOpenFilterDropdowns(newOpen);
  };
  const handleAddFilter = (filterId: string) => {
    const newAdded = new Set(addedFilters);
    newAdded.add(filterId);
    setAddedFilters(newAdded);
  };
  const handleClearAllFilters = () => {
    setFilterValues({});
    setAddedFilters(new Set());
    setSearchQuery('');
  };
  // Helper for progress bar color
  const getProgressColor = (claimed: number, total: number) => {
    if (total === 0) return 'bg-ocean-gray-200';
    const percentage = claimed / total * 100;
    if (percentage > 95) return 'bg-[#EF4444]'; // Red
    if (percentage >= 80) return 'bg-[#F59E0B]'; // Amber
    return 'bg-[#10B981]'; // Green
  };
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Draft':
        return 'default';
      // Grey
      case 'Closed':
        return 'default';
      // Slate/Grey
      default:
        return 'default';
    }
  };
  // Bulk Actions Logic
  const selectedAllocations = mockAllocations.filter(a => selectedIds.has(a.id));
  const hasActiveSelected = selectedAllocations.some(a => a.status === 'Active');
  const hasDraftSelected = selectedAllocations.some(a => a.status === 'Draft');
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-6659" data-mp="p69xhzp0wixg">
      {/* NavBar */}
      <div className="mb-6" data-id="element-6660" data-mp="u1yqtlag93lb">
        <NavBar tabs={[]} title="Allocations" data-id="element-6661" data-mp="u5cq2kntgwoj" />
      </div>

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-6662" data-mp="19ki3wtpuy9xk">
        <div className="flex-1" data-id="element-6663" data-mp="14k3vvbo9azzj">
          {/* Breadcrumbs or description could go here */}
        </div>
        <div className="flex items-center gap-3" data-id="element-6664" data-mp="10mfy80v6it3y">
          <ExportButton onExport={() => console.log('Exporting allocations')} data-id="element-6665" data-mp="vkgu2orm7s5y" />
          <Button variant="primary" iconLeft={<Plus className="w-4 h-4" data-id="element-6667" data-mp="qn751a851iin" />} onClick={() => navigate('/allocations/create')} data-id="element-6666" data-mp="qmyuhozhmpam">
            Create Allocation
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg mb-6 p-4 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-6668" data-mp="gxnl018ovim7">
        <div className="flex flex-col gap-4" data-id="element-6669" data-mp="cwlnbouf7emc">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-6670" data-mp="7vo4da1l6gjs">
            <div className="w-full lg:w-80 flex-shrink-0" data-id="element-6671" data-mp="2wutzqhlj5nu">
              <SearchBar placeholder="Search allocations..." value={searchQuery} onChange={setSearchQuery} className="w-full" data-id="element-6672" data-mp="2xctcvzk1zzz" />
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-shrink-0 w-full sm:w-auto sm:ml-auto" data-id="element-6673" data-mp="2jj14fl112a5o">
              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-6674" data-mp="2ojp5z0mvu9e5" />
            </div>
          </div>

          {addedFilters.size > 0 && <div className="flex items-center gap-3" data-id="element-6675" data-mp="20t1iv84ebd70">
              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-6676" data-mp="24tv5zza0knyu" />
              <button onClick={handleClearAllFilters} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-6677" data-mp="1d14hyc2hi5ko">
                Clear all
              </button>
            </div>}
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && <div className="bg-white border border-[#E2E8F0] rounded-lg px-6 py-3 mb-6 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-6678" data-mp="1bs711inmot9b">
          <div className="flex items-center gap-2" data-id="element-6679" data-mp="1kslwvby7xqr2">
            <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-6680" data-mp="o58ogw2mq5go">
              {selectedIds.size} allocation{selectedIds.size !== 1 ? 's' : ''}{' '}
              selected
            </span>
            <div className="h-4 w-px bg-[#E2E8F0] mx-2" data-id="element-6681" data-mp="x4vmd7w5gt4z" />
            <button onClick={() => setSelectedIds(new Set())} className="text-[13px] text-[#64748B] hover:text-[#0F172A] transition-colors" data-id="element-6682" data-mp="x3arti9u5a74">
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-3" data-id="element-6683" data-mp="9fjimnjldda2">
            <Button variant="outline" size="sm" iconLeft={<Download className="w-4 h-4" data-id="element-6685" data-mp="1w0fqwqu0pak" />} data-id="element-6684" data-mp="9e8cwi6l5vgb">
              Export Selected
            </Button>
            {hasActiveSelected && <Button variant="outline" size="sm" iconLeft={<XCircle className="w-4 h-4" data-id="element-6687" data-mp="3t34lzpf5k7hx" />} data-id="element-6686" data-mp="3t1tk5v59p9tg">
                Close Allocations
              </Button>}
            {hasDraftSelected && <Button variant="danger" size="sm" iconLeft={<Trash2 className="w-4 h-4" data-id="element-6689" data-mp="3k0ll3auxg4yb" />} data-id="element-6688" data-mp="3o534h8xdkg8k">
                Delete
              </Button>}
          </div>
        </div>}

      {/* Data Table */}
      <Card padding="none" className="overflow-hidden" data-id="element-6690" data-mp="3f4n2y506zj6c">
        <div className="overflow-x-auto" data-id="element-6691" data-mp="3aaskt608jc9d">
          <table className="w-full" data-id="element-6692" data-mp="3a9r16d55lhg1">
            <thead className="bg-[#F1F5F9]" data-id="element-6693" data-mp="35c7hgxposcgp">
              <tr data-id="element-6694" data-mp="35d0hlfu543pv">
                <th className="w-12 px-6 py-3 border-b border-[#E2E8F0]" data-id="element-6695" data-mp="35ae9nf3c7m74">
                  <div className="flex items-center justify-center" data-id="element-6696" data-mp="30fh6gosqo725">
                    <input type="checkbox" className="w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer" checked={selectedIds.size === filteredAllocations.length && filteredAllocations.length > 0} onChange={handleSelectAll} data-id="element-6697" data-mp="2wb0zek7e2tlc" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6698" data-mp="2lrqjfj0abex5">
                  Allocation
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6699" data-mp="2cpr0gl5pm0rk">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6700" data-mp="30hy3u49ur80h">
                  Products
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6701" data-mp="2qp0f3ge9omtf">
                  Window
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6702" data-mp="3jezbjwusjm84">
                  Claimed
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6703" data-mp="3o6qtxk3smln6">
                  Revenue
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6704" data-mp="17s5hq5bn0skf">
                  Eligible
                </th>
                <th className="px-6 py-3 w-10" data-id="element-6705" data-mp="1hh5l00x6aa4f"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]" data-id="element-6706" data-mp="1hjjew6g49gae">
              {filteredAllocations.map(allocation => {
              const isSelected = selectedIds.has(allocation.id);
              const percentage = allocation.totalAvailable > 0 ? Math.round(allocation.claimed / allocation.totalAvailable * 100) : 0;
              return <tr key={allocation.id} className={`hover:bg-[#F8FAFC] transition-colors ${isSelected ? 'bg-[#F0F9FF]' : ''}`} data-id="element-6707" data-mp="xtfz2abx3434">
                    <td className="px-6 py-4" data-id="element-6708" data-mp="2e6eobhxns996">
                      <div className="flex items-center justify-center" data-id="element-6709" data-mp="2e997l64xro10">
                        <input type="checkbox" className="w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer" checked={isSelected} onChange={() => handleSelectRow(allocation.id)} data-id="element-6710" data-mp="2983c50jw5mns" />
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6711" data-mp="3efnze9232l37">
                      <div data-id="element-6712" data-mp="3ej243ny7z6dt">
                        <button onClick={() => navigate(`/allocations/${allocation.id}`)} className="text-[14px] font-bold text-[#0F172A] hover:text-[#0EA5E9] transition-colors text-left" data-id="element-6713" data-mp="3jgbsr1dsbe0l">
                          {allocation.name}
                        </button>
                        <div className="text-[13px] text-[#64748B] mt-0.5 truncate max-w-[280px]" data-id="element-6714" data-mp="3se46i13uydlv">
                          {allocation.description}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6715" data-mp="b0iktaxirav5">
                      <Badge variant={getStatusVariant(allocation.status)} data-id="element-6716" data-mp="b1tnt8j54el7">
                        {allocation.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4" data-id="element-6717" data-mp="1zko6ucglfmcq">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0]" data-id="element-6718" data-mp="1zha3p203v948">
                        {allocation.productCount}{' '}
                        {allocation.productCount === 1 ? 'wine' : 'wines'}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-6719" data-mp="2j6kga74k1dy4">
                      <div className="text-[14px] text-[#0F172A]" data-id="element-6720" data-mp="2j9ezjvbu0spy">
                        {allocation.startDate === 'TBD' ? 'TBD' : <>
                            {allocation.startDate}{' '}
                            {allocation.endDate && `– ${allocation.endDate}`}
                          </>}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6721" data-mp="31yvehno9knpw">
                      <div className="w-full max-w-[140px]" data-id="element-6722" data-mp="31zwnlmlpz04z">
                        <div className="flex justify-between text-[13px] mb-1" data-id="element-6723" data-mp="2x5siz9zxf472">
                          <span className="font-medium text-[#0F172A]" data-id="element-6724" data-mp="vaw5f3mn1soy">
                            {allocation.claimed} / {allocation.totalAvailable}
                          </span>
                          <span className="text-[#64748B]" data-id="element-6725" data-mp="lkjjb6z6drgp">{percentage}%</span>
                        </div>
                        <div className="h-1.5 w-full bg-[#E2E8F0] rounded-full overflow-hidden" data-id="element-6726" data-mp="qibfwearm1r8">
                          <div className={`h-full rounded-full ${getProgressColor(allocation.claimed, allocation.totalAvailable)}`} style={{
                        width: `${percentage}%`
                      }} data-id="element-6727" data-mp="blkkeg47pa6w" />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-6728" data-mp="7jgysyntwp7w">
                      <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-6729" data-mp="7m3exmoscd1q">
                        ${allocation.revenue.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-6730" data-mp="21vxculmv97nb">
                      <span className="text-[14px] text-[#475569]" data-id="element-6731" data-mp="2c2zyb93oeu6p">
                        {allocation.eligibleCount}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-6732" data-mp="2kv1x72y3k2ev">
                      <button className="text-[#94A3B8] hover:text-[#0F172A] transition-colors" data-id="element-6733" data-mp="2kvtfjc2oo3n7">
                        <MoreVertical className="w-4 h-4" data-id="element-6734" data-mp="2g2iho0fumx7f" />
                      </button>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-[#E2E8F0]" data-id="element-6735" data-mp="2pvzezef72ufu">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-6736" data-mp="33sun94nmghuu">
            <div className="text-[13px] text-[#64748B]" data-id="element-6737" data-mp="2zt3zl9te6huj">
              Showing 1–{filteredAllocations.length} of{' '}
              {filteredAllocations.length} allocations
            </div>
            <div className="flex items-center gap-2" data-id="element-6738" data-mp="15etb52gplppa">
              <button disabled className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-6739" data-mp="1typw49p3vwua">
                Previous
              </button>
              <div className="flex items-center gap-1" data-id="element-6740" data-mp="23p1a6p2i7mew">
                <button className="w-10 h-10 rounded-md text-[13px] font-medium bg-[#0EA5E9] text-white transition-colors" data-id="element-6741" data-mp="27qmkq3rkfolx">
                  1
                </button>
              </div>
              <button disabled className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-6742" data-mp="1fynhcwc3hjj9">
                Next
              </button>
            </div>
          </div>
        </div>
      </Card>
    </div>;
}